import {useCallback, useEffect, useMemo, useState} from 'react';
import {Box, Button, Skeleton, Stack, Typography} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import {SplitTabsPanel} from '@/components/common';
import {
    AuditProgramSectionVO,
    getOSectionVO,
    saveOSectionPage,
} from '@/services/auditService/auditProgramService';
import {
    O0_PAGE,
    O1_PAGE,
    O2_PAGE,
    O_1_PAGE,
    O_PAGE_TITLES,
    O_SECTION_PAGES,
    O1Page,
    O_1Page,
    OProgrammePage,
    OSectionPageCode,
} from '@/pages/components/audit-program/o-section/constants';
import O1ShareCapitalView from '@/pages/components/audit-program/o-section/O1ShareCapitalView';
import O_1EquityView from '@/pages/components/audit-program/o-section/O_1EquityView';
import G0ProgrammeView from '@/pages/components/audit-program/g-section/G0ProgrammeView';
import PageTabLabel from '@/pages/components/audit-program/PageTabLabel';
import {useProgramSectionTemplates} from '@/pages/components/audit-program/templates/useProgramSectionTemplates';

export type OSectionViewProps = {
    companyId: number;
    annualId: number;
    companyName?: string | null;
    initialPage?: OSectionPageCode;
};

function toStringRecord(data?: Record<string, unknown>): Record<string, string> {
    if (!data) return {};
    return Object.fromEntries(
        Object.entries(data).map(([k, v]) => [k, v == null ? '' : String(v)]),
    );
}

function withHeaderDefaults(
    page: Record<string, string>,
    data: AuditProgramSectionVO,
    refCode: string,
): Record<string, string> {
    return {
        ...page,
        client: page.client || data.client || '',
        yearEnd: page.yearEnd || data.yearEnd || '',
        fileNo: page.fileNo || data.fileNo || '',
        preparedBy: page.preparedBy || data.preparedInitial || data.preparedBy || '',
        completedBy: page.completedBy || data.preparedInitial || data.preparedBy || '',
        reviewedBy: page.reviewedBy || data.reviewedInitial || data.reviewedBy || '',
        ref: page.ref || refCode,
    };
}

export default function OSectionView({
    companyId,
    annualId,
    companyName,
    initialPage = 'O0',
}: OSectionViewProps) {
    const [pageKey, setPageKey] = useState<OSectionPageCode>(initialPage);
    const [header, setHeader] = useState<AuditProgramSectionVO | null>(null);
    const [pageData, setPageData] = useState<Record<string, Record<string, string>>>({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dirty, setDirty] = useState<Record<string, boolean>>({});
    const {getContent} = useProgramSectionTemplates('O');

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getOSectionVO(companyId, annualId);
            if (companyName && !data.client) {
                data.client = companyName;
            }
            setHeader(data);
            const pages: Record<string, Record<string, string>> = {};
            for (const code of O_SECTION_PAGES) {
                pages[code] = withHeaderDefaults(toStringRecord(data.pages?.[code]), data, code);
            }
            setPageData(pages);
            setDirty({});
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to load O Section';
            setError(msg);
            setHeader(null);
        } finally {
            setLoading(false);
        }
    }, [companyId, annualId, companyName]);

    useEffect(() => {
        void load();
    }, [load]);

    useEffect(() => {
        setPageKey(initialPage);
    }, [initialPage]);

    const handleFieldChange = useCallback((pageCode: OSectionPageCode, key: string, value: string) => {
        setPageData((prev) => ({
            ...prev,
            [pageCode]: {...(prev[pageCode] ?? {}), [key]: value},
        }));
        setDirty((prev) => ({...prev, [pageCode]: true}));
    }, []);

    const handleSave = useCallback(async () => {
        if (!dirty[pageKey]) return;
        setSaving(true);
        setError(null);
        try {
            await saveOSectionPage(companyId, annualId, pageKey, pageData[pageKey] ?? {});
            setDirty((prev) => ({...prev, [pageKey]: false}));
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to save';
            setError(msg);
        } finally {
            setSaving(false);
        }
    }, [companyId, annualId, dirty, pageKey, pageData]);

    const o0Content = (getContent('O0') ?? O0_PAGE) as OProgrammePage;
    const o1Content = (getContent('O1') ?? O1_PAGE) as O1Page;
    const o2Content = (getContent('O2') ?? O2_PAGE) as OProgrammePage;
    const o1uContent = (getContent('O_1') ?? O_1_PAGE) as O_1Page;

    const tabs = useMemo(() => O_SECTION_PAGES.map((code) => ({
        key: code,
        label: <PageTabLabel code={code} title={O_PAGE_TITLES[code]}/>,
        icon: code,
        content: header ? (
            code === 'O0' || code === 'O2' ? (
                <G0ProgrammeView
                    header={header}
                    page={code === 'O0' ? o0Content : o2Content}
                    refCode={code}
                    values={pageData[code] ?? {}}
                    onChange={(k, v) => handleFieldChange(code, k, v)}
                />
            ) : code === 'O1' ? (
                <O1ShareCapitalView
                    header={header}
                    page={o1Content}
                    values={pageData.O1 ?? {}}
                    onChange={(k, v) => handleFieldChange('O1', k, v)}
                />
            ) : (
                <O_1EquityView
                    header={header}
                    page={o1uContent}
                    values={pageData.O_1 ?? {}}
                    onChange={(k, v) => handleFieldChange('O_1', k, v)}
                />
            )
        ) : null,
    })), [header, pageData, handleFieldChange, o0Content, o1Content, o2Content, o1uContent]);

    if (loading) {
        return <Skeleton variant="rectangular" height={480} sx={{m: 2}}/>;
    }

    if (error && !header) {
        return (
            <Box sx={{p: 2}}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column'}}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{px: 2, pt: 1, pb: 0.5}}>
                <Typography variant="subtitle1" fontWeight={600}>
                    O Section — Share capital, reserves and statutory records
                </Typography>
                <Button
                    size="small"
                    variant="contained"
                    startIcon={<SaveIcon/>}
                    disabled={!dirty[pageKey] || saving}
                    onClick={() => void handleSave()}
                >
                    {saving ? 'Saving…' : 'Save'}
                </Button>
            </Stack>
            {error ? (
                <Typography color="error" variant="body2" sx={{px: 2}}>{error}</Typography>
            ) : null}
            <Box sx={{flex: 1, minHeight: 0}}>
                <SplitTabsPanel
                    tabs={tabs}
                    selectedKey={pageKey}
                    onChange={(k) => setPageKey(k as OSectionPageCode)}
                    height="100%"
                    allowMaximize={false}
                    contentWrapperSx={{height: '100%', overflow: 'auto', p: 0}}
                    rightPaneSx={{height: '100%', overflow: 'hidden'}}
                />
            </Box>
        </Box>
    );
}
